// Firebase Configuration - Push Notifications + Firestore (Real-time Messaging) + Auth
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { initializeFirestore, getFirestore, persistentLocalCache, memoryLocalCache } from 'firebase/firestore';
import { getAuth, signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Initialize Firebase (singleton pattern)
let firebaseApp = null;
let messagingInstance = null;
let firestoreInstance = null;
let authInstance = null;
let isFirebaseAuthenticated = false;

// Detect Safari/iOS (has issues with IndexedDB persistence causing "ReadableStream is locked" errors)
const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
         /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export function getFirebaseApp() {
  if (!firebaseApp && getApps().length === 0) {
    firebaseApp = initializeApp(firebaseConfig);
  }
  return firebaseApp || getApp();
}

// Firestore for real-time messaging
// Uses initializeFirestore with settings to avoid race conditions
// iOS/Safari uses memoryLocalCache to prevent "ReadableStream is locked" errors
// Other browsers use persistentLocalCache for offline support
export function getFirestoreDb() {
  if (!firestoreInstance) {
    const app = getFirebaseApp();
    const useSafariMode = isSafari();
    
    try {
      // Try to initialize with our preferred settings
      console.log(`[Firestore] Initializing with ${useSafariMode ? 'memory' : 'persistent'} cache`);
      firestoreInstance = initializeFirestore(app, {
        localCache: useSafariMode ? memoryLocalCache() : persistentLocalCache()
      });
    } catch (error) {
      // If already initialized (hot reload, strict mode), get the existing instance
      if (error.code === 'failed-precondition' || error.message?.includes('already been called')) {
        console.log('[Firestore] Already initialized, using existing instance');
        firestoreInstance = getFirestore(app);
      } else {
        throw error;
      }
    }
  }
  return firestoreInstance;
}

export function getFirebaseMessaging() {
  if (!messagingInstance) {
    const app = getFirebaseApp();
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
}

// Firebase Auth instance
export function getFirebaseAuth() {
  if (!authInstance) {
    const app = getFirebaseApp();
    authInstance = getAuth(app);
  }
  return authInstance;
}

/**
 * Sign into Firebase using a custom token from our backend.
 * This links the Supabase user ID to Firebase Auth, enabling Firestore security rules.
 * 
 * @param {Object} user - The Supabase user object with id, church_id, role, is_super_admin
 * @returns {Promise<boolean>} - True if sign-in successful
 */
export async function signIntoFirebase(user, _isRetry = false) {
  if (!user?.id) {
    console.error('[Firebase Auth] No user ID provided');
    return false;
  }

  // Skip if already authenticated with this user
  const auth = getFirebaseAuth();
  if (auth.currentUser?.uid === user.id) {
    console.log('[Firebase Auth] Already signed in as:', user.id);
    isFirebaseAuthenticated = true;
    return true;
  }

  try {
    console.log('[Firebase Auth] Requesting custom token for user:', user.id);
    
    // Get custom token from backend
    const response = await fetch(`${API_URL}/api/firebase/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: user.id,
        church_id: user.church_id || null,
        role: user.role || 'member',
        is_super_admin: user.is_super_admin || false
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      console.error('[Firebase Auth] Failed to get token:', error);
      throw new Error(error.detail || 'Token request failed');
    }

    const { token } = await response.json();
    
    // Sign into Firebase with the custom token
    console.log('[Firebase Auth] Signing in with custom token...');
    await signInWithCustomToken(auth, token);
    
    isFirebaseAuthenticated = true;
    if (typeof window !== 'undefined') window.__fbAuthStatus = 'authenticated';
    console.log('[Firebase Auth] Successfully signed in as:', auth.currentUser?.uid);
    return true;
    
  } catch (error) {
    console.error('[Firebase Auth] Sign-in error:', error);
    isFirebaseAuthenticated = false;
    if (typeof window !== 'undefined') window.__fbAuthStatus = `failed: ${error.message}`;

    // One retry with delay for transient network failures
    if (!_isRetry) {
      console.log('[Firebase Auth] Retrying in 2 seconds...');
      await new Promise(r => setTimeout(r, 2000));
      return signIntoFirebase(user, true);
    }

    return false;
  }
}

/**
 * Sign out of Firebase Auth.
 * Should be called when user logs out of Supabase.
 */
export async function signOutOfFirebase() {
  try {
    const auth = getFirebaseAuth();
    await signOut(auth);
    isFirebaseAuthenticated = false;
    console.log('[Firebase Auth] Signed out');
  } catch (error) {
    console.error('[Firebase Auth] Sign-out error:', error);
  }
}

/**
 * Check if user is authenticated with Firebase.
 * @returns {boolean}
 */
export function isFirebaseSignedIn() {
  return isFirebaseAuthenticated && getFirebaseAuth().currentUser !== null;
}

/**
 * Get the current Firebase user.
 * @returns {User|null}
 */
export function getCurrentFirebaseUser() {
  return getFirebaseAuth().currentUser;
}

/**
 * Listen for Firebase auth state changes.
 * @param {Function} callback - Called with (user) when auth state changes
 * @returns {Function} - Unsubscribe function
 */
export function onFirebaseAuthChange(callback) {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, (user) => {
    isFirebaseAuthenticated = user !== null;
    callback(user);
  });
}

// Request notification permission and get FCM token
export async function requestNotificationPermission(userId) {
  try {
    console.log('[Push] Starting notification permission request for user:', userId);
    
    // Check if push is supported
    if (!isPushSupported()) {
      console.error('[Push] Push notifications not supported in this browser');
      return null;
    }
    
    // Request permission
    console.log('[Push] Requesting notification permission...');
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);
    
    if (permission !== 'granted') {
      console.log('[Push] Notification permission denied');
      return null;
    }
    
    // Register service worker
    console.log('[Push] Registering service worker...');
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('[Push] Service Worker registered:', registration.scope);
      
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('[Push] Service Worker is ready');
    } catch (swError) {
      console.error('[Push] Service Worker registration failed:', swError);
      return null;
    }
    
    // Get FCM token
    console.log('[Push] Getting FCM token...');
    const messaging = getFirebaseMessaging();
    
    let token;
    try {
      token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      });
    } catch (tokenError) {
      console.error('[Push] Failed to get FCM token:', tokenError);
      // Common errors:
      // - messaging/token-subscribe-failed: Usually a VAPID key issue
      // - messaging/permission-blocked: Permission was denied
      return null;
    }
    
    if (token) {
      console.log('[Push] FCM Token obtained:', token.substring(0, 30) + '...');
      
      // Send token to backend
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      console.log('[Push] Sending token to backend...');
      
      try {
        const response = await fetch(`${API_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            token: token,
            device_info: navigator.userAgent
          })
        });
        
        if (response.ok) {
          console.log('[Push] Token registered with backend successfully');
        } else {
          const errorText = await response.text();
          console.error('[Push] Failed to register token with backend:', response.status, errorText);
        }
      } catch (fetchError) {
        console.error('[Push] Network error registering token:', fetchError);
      }
      
      return token;
    } else {
      console.error('[Push] No FCM token returned');
      return null;
    }
  } catch (error) {
    console.error('[Push] Error in requestNotificationPermission:', error);
    return null;
  }
}

/**
 * CRITICAL FOR iOS: Refresh push subscription on every app launch
 * iOS aggressively expires push subscriptions. This function checks
 * the current subscription and re-sends it to the server to keep it alive.
 */
export async function refreshPushSubscription(userId) {
  try {
    if (!isPushSupported()) {
      return null;
    }
    
    // Check if we have permission
    if (Notification.permission !== 'granted') {
      console.log('[Push] No notification permission, skipping refresh');
      return null;
    }
    
    const registration = await navigator.serviceWorker.ready;
    const messaging = getFirebaseMessaging();
    
    // Get the current token - this will refresh it if needed
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    if (token && userId) {
      console.log('[Push] Refreshing subscription with server...');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          token: token,
          device_info: navigator.userAgent
        })
      });
      
      console.log('[Push] Subscription refreshed successfully');
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('[Push] Error refreshing subscription:', error);
    return null;
  }
}

// Listen for foreground messages
export function onForegroundMessage(callback) {
  const messaging = getFirebaseMessaging();
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}
